import api from '@api/endpoints';
import { ALERT_STATUS_TYPE, KYC_PROVIDER } from '@constants';
import { getErrorMessage } from '@utils';
import { KYC as types } from '@store/constants';
import { setAlert } from '@store/actions/alertActions';


export const getKycDetailsAndDocs =
  ({ userId, payload }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.GETTING_KYC_DETAILS
      });

      const { data } = await api.kyc.getKycDetailsAndDocs({
        userId,
        payload
      });

      return dispatch({
        type: types.GET_KYC_DETAILS_SUCCESS,
        payload: data
      });
    } catch (error) {
      dispatch({
        type: types.GET_KYC_DETAILS_FAILURE,
        payload: error?.message || "Error: Unable to fetch User KYC Details"
      });
    }
  };

  export const getAllKycsV2 = (searchRequest) => async (dispatch) => {
    try {
      dispatch({ type: types.GETTING_ALL_KYCS });

      const requestBody = {
        provider: searchRequest.provider,
        subProvider: searchRequest.subProviders,
        product: searchRequest.product,
        status: searchRequest.status || null,
        page: searchRequest.pageNo || 0,
        pageSize: searchRequest.pageSize || 10,
        tags: Array.isArray(searchRequest.tags) ? searchRequest.tags : [],
        unselectedTags: Array.isArray(searchRequest.unselectedTags) ? searchRequest.unselectedTags : [],
        country: searchRequest.country ? searchRequest.country : null,
        userId: searchRequest.userId || null,
      };

      const response = await api.kyc.allKycsV2(requestBody);
      const data = response.data;  
      dispatch({
        type: types.GET_ALL_KYCS_SUCCESS,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: types.GET_ALL_KYCS_FAILURE,
        payload: error.message,
      });
    }
  };

export const getKycChecks =
  ({ userId, pageNo, pageSize }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.GETTING_KYC_CHECKS
      });

      const { data } = await api.kyc.checks({ pageNo, pageSize, userId });

      return dispatch({
        type: types.GET_KYC_CHECKS_SUCCESS,
        payload: data
      });
    } catch (error) {
      dispatch({
        type: types.GET_KYC_CHECKS_FAILURE,
        payload: error
      });
    }
  };

export const updateKycDocStatus =
  ({ userId, formData, onFinish, onSuccess, onFailure }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.UPDATING_KYC_DOC_STATUS
      });

      const { data } = await api.kyc.updateKycStatusV2(userId, formData);

      dispatch({
        type: types.UPDATE_KYC_DOC_STATUS_SUCCESS,
        payload: data
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: types.UPDATE_KYC_DOC_STATUS_FAILURE,
        payload: error
      });
      const errorMessage = error.data
        ? JSON.parse(error.data)?.message
        : error.message;
      if (onFailure) onFailure(errorMessage);
    } finally {
      if (onFinish) onFinish();
    }
  };

export function getUserKycStatus({ userId, pageNo = 0, pageSize = 3, provider, subProvider }) {
  return async function (dispatch) {
    try {
      let data = null;
      if (provider === KYC_PROVIDER.EFR) {
        const res = await api.kyc.userKycStatus({ userId, pageNo, pageSize });
        data = res.data;
      } else if (provider === KYC_PROVIDER.SUMSUB) {
        const res = await api.kyc.userKycStatusSumsub({ userId });
        data = res.data;
      } else {
        data = { data: [], totalPages: 0 };
      }
      return data;
    } catch (error) {
      if (error.status === 404) return error;
      else {
        dispatch(
          setAlert({
            title: `${error.message}`,
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        throw error;
      }
    }
  };
}

export const syncProviderKyc =
  ({ userId, product, provider, subProvider, onSuccess, onFinish }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.SYNCING_USER_KYC
      });

      const { data } = await api.kyc.syncUserKyc({ userId, product, provider, subProvider });

      dispatch({
        type: types.SYNC_USER_KYC_SUCCESS,
        payload: data
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(
        setAlert({
          title: getErrorMessage({
            error,
            genericMessage: 'Failed to sync user kyc!'
          })
        })
      );
      dispatch({
        type: types.SYNC_USER_KYC_FAILURE,
        payload: error
      });
    } finally {
      if (onFinish) onFinish();
    }
  };

export const syncVanceUserKyc =
  ({ userId, product, onSuccess, onFinish }) =>
  async (dispatch) => {
    try {
      await api.kyc.syncVanceUserKyc({ userId, product });

      if (onSuccess) onSuccess();

      dispatch(
        setAlert({
          title: 'Successfully synced vance user kyc!',
          status: 'success'
        })
      );
    } catch (error) {
      dispatch(
        setAlert({
          title: getErrorMessage({
            error,
            genericMessage: 'Failed to sync vance user kyc!'
          })
        })
      );
    } finally {
      if (onFinish) onFinish();
    }
  };

export const updateCheckStatus =
  ({ checkId, onSuccess, onFinish }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.UPDATING_CHECK_STATUS
      });

      await api.kyc.updateCheckStatus({ checkId });

      dispatch({
        type: types.UPDATE_CHECK_STATUS_SUCCESS
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(
        setAlert({
          title: getErrorMessage({
            error,
            genericMessage: 'Failed to updated Check Status'
          }),
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      dispatch({
        type: types.UPDATE_CHECK_STATUS_FAILURE,
        payload: error
      });
    } finally {
      if (onFinish) onFinish();
    }
  };

export const createEKyc =
  ({ userId, onFinish, onSuccess }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.CREATING_E_KYC
      });

      await api.kyc.createEKyc({ userId });

      dispatch({
        type: types.CREATE_E_KYC_SUCCESS
      });

      dispatch(
        setAlert({
          title: 'E Kyc created!',
          status: 'success'
        })
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(
        setAlert({
          title: getErrorMessage({
            error,
            genericMessage: 'Failed to create E Kyc!'
          }),
          status: ALERT_STATUS_TYPE.ERROR
        })
      );

      dispatch({
        type: types.CREATE_E_KYC_FAILURE
      });
    } finally {
      if (onFinish) onFinish();
    }
  };

export const createMKyc =
  ({ userId, onSuccess, onFinish }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: types.CREATING_M_KYC
      });

      await api.kyc.createMKyc({ userId });

      dispatch({
        type: types.CREATE_M_KYC_SUCCESS
      });

      dispatch(
        setAlert({
          title: 'M Kyc created!',
          status: 'success'
        })
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(
        setAlert({
          title: getErrorMessage({
            error,
            genericMessage: 'Failed to create E Kyc!'
          }),
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      dispatch({
        type: types.CREATE_M_KYC_FAILURE
      });
    } finally {
      if (onFinish) onFinish();
    }
  };

export const clearKycDetails = () => ({
  type: types.CLEAR_KYC_DETAILS
});

export const clearKycChecks = () => ({
  type: types.CLEAR_KYC_CHECKS
});

export const clearAllKycs = () => ({
  type: types.CLEAR_ALL_KYCS
});

export const rejectEfrKyc =
  ({ userId, provider, product, subProvider, onSuccess, onComplete }) =>
  async (dispatch) => {
    try {
      await api.kyc.rejectEfrKyc({ userId, data: { product, provider, subProvider } });
      onSuccess && onSuccess();
    } catch (error) {
      dispatch(
        setAlert({
          title: error.message,
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
    } finally {
      onComplete && onComplete();
    }
  };
export const rejectEFR =
  ({ userId, onSuccess, onComplete }) =>
  async (dispatch) => {
    try {
      await api.kyc.rejectEfr({ userId });
      dispatch(
        setAlert({
          title: 'Rejection Successful',
          status: 'success'
        })
      );
      onSuccess && onSuccess();
    } catch (error) {
      dispatch(
        setAlert({
          title: error.message,
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
    } finally {
      onComplete && onComplete();
    }
  };

  export const getKycTags =
  ({ onComplete } = {}) =>
  async (dispatch) => {
    dispatch({ type: types.GETTING_KYC_TAGS });

    try {
      const tags = await api.kyc.kycTags(); // returns response with .data
      dispatch({
        type: types.GET_KYC_TAGS_SUCCESS,
        payload: tags.data
      });
    } catch (error) {
      dispatch({
        type: types.GET_KYC_TAGS_FAILURE,
        payload: error.message || 'Failed to fetch tags'
      });

      dispatch(
        setAlert({
          title: error.message || 'Failed to fetch tags',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
    } finally {
      if (onComplete) onComplete();
    }
  };

export const getUserTiers =
  ({ userId }) =>
    async (dispatch) => {
      try {
        dispatch({
          type: types.GETTING_KYC_TIERS
        });

        const { data } = await api.kyc.getUserTiers({ userId });

        dispatch({
          type: types.GET_KYC_TIERS_SUCCESS,
          payload: data
        });

      } catch (error) {
        dispatch({
          type: types.GET_KYC_TIERS_SUCCESS,
          payload: error
        });
      }
    };